import {
  DocumentTaxSchema,
  ExtractDataFromExpense,
  LineItemSchema,
  LineItemTaxSchema,
  ZodCreateCreditNoteSchema,
} from "@billingnow/zod-schema";
import { calculateTaxAmount } from "@billingnow/utilities/client";
import { Invoice, Tax } from "@billingnow/rest-api-client";
import { de, enUS, es, fr, it } from "date-fns/locale";
import { BadgeVariants } from "@/components/ui/badge";
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { addDays } from "date-fns";
import { AxiosError } from "axios";

export * from "./chat";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const formatCurrencyText = (currency: string, amount?: number, showCurrency = true) => {
  return `${showCurrency ? currency + " " : ""}${(amount || 0)?.toFixed(2)}`;
};

export const parseCatchError = (error: Error | AxiosError<string> | unknown, message?: string) => {
  return (
    ((error as AxiosError<{ success: boolean }>)?.response?.data.success === false
      ? message
      : (error as AxiosError<string>)?.response?.data || message || (error as Error)?.message) ||
    "Unknown error occurred"
  );
};

/**
 * Converts invoice data to credit note format
 * @param invoice The invoice to convert
 * @returns ZodCreateCreditNoteSchema formatted data
 */
export const convertInvoiceToCreditNote = (invoice: Invoice, creditNoteNumber?: string): ZodCreateCreditNoteSchema => {
  return {
    invoiceIds: [invoice.id],
    ...invoice,
    date: new Date(), // Current date for credit note
    dueDate: addDays(new Date(), 30), // Credit notes typically don't have due dates
    creditNoteNumber: creditNoteNumber || "",
    items: [],
    // metadata: {
    //   notes: `Credit note for invoice ${invoice.documentFields.documentDetails.documentNumber}`,
    //   // terms: invoice.documentFields.metadata.terms,
    //   // paymentInformation: invoice.documentFields.metadata.paymentInformation,
    //   terms: "",
    //   paymentInformation: [],
    // },
  };
};

export const getStatusBadgeVariant = (status: string): BadgeVariants => {
  switch (status) {
    case "draft":
      return "gray";
    case "not-sent":
      return "orange";
    case "sent":
      return "blue";
    case "overdue":
      return "pink";
    case "partially-paid":
      return "yellow";
    case "paid":
      return "green";
    case "cancelled":
      return "destructive";

    case "approved":
      return "green";
    case "rejected":
      return "pink";
    case "expired":
      return "pink";

    case "pending":
      return "orange";

    case "credited":
      return "green";
    case "debited":
      return "destructive";

    case "invoice":
    case "estimate":
    case "credit-note":
    case "expense":
    case "payment":
      return "secondary";

    case "active":
      return "green";
    default:
      return "gray";
  }
};

export const getPlanVariant = (plan: string) => {
  switch (plan) {
    case "lite":
      return "gray";
    case "plus":
      return "blue";
    case "premium":
      return "purple";
    case "enterprise":
      return "yellow";
    default:
      return "gray";
  }
};

interface FormatAddressProps {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  country?: string | null;
}
export const formatAddress = (props: FormatAddressProps) => {
  const { addressLine1, addressLine2, city, state, zipcode, country } = props;

  return `${addressLine1 ? `${addressLine1}, ` : ""}${addressLine2 ? `${addressLine2}, ` : ""}${addressLine1 || addressLine2 ? "\n" : ""}${
    city ? `${city}, ` : ""
  }${state ? `${state}` : ""}${zipcode ? ` ${zipcode}` : ""}${city || state || zipcode ? "\n" : ""}${country ? `, ${country}` : ""}`;
};

export const taxToDocumentTax = (taxes: Tax[]): DocumentTaxSchema[] => {
  return taxes?.map((tax) => ({
    id: tax.id,
    taxDesignation: tax.taxDesignation,
    taxRate: tax.taxRate,
    isDefault: tax.isDefault,
    cumulativeTaxes: taxToDocumentTax(tax.cumulativeTaxes || []),
  }));
};

export const documentTaxToCumulativeTax = (tax: DocumentTaxSchema): LineItemTaxSchema => {
  return {
    id: tax.id,
    taxDesignation: tax.taxDesignation,
    taxRate: tax.taxRate,
    taxValue: 0,
    cumulativeTaxes: tax.cumulativeTaxes?.map(documentTaxToCumulativeTax) || [],
  };
};

export const formatTaxes = (
  item: LineItemSchema,
  showItemsWithTax: boolean,
  taxes: LineItemTaxSchema[],
): LineItemTaxSchema[] =>
  sortTaxesByCumulative(taxes).map((tax) => {
    const taxValue = calculateTaxAmount({
      amount: item.netPrice,
      quantity: item.quantity,
      isNetPrice: true,
      tax,
      taxes,
      discount: item.discount,
      discountType: item.discountType,
    });

    return {
      ...tax,
      taxValue,
      cumulativeTaxes: formatTaxes(item, showItemsWithTax, tax.cumulativeTaxes || []),
    };
  });

export function sortTaxesByCumulative(taxes: LineItemTaxSchema[]) {
  const map = new Map<string, LineItemTaxSchema>();
  taxes.forEach((t) => map.set(t.id, t));

  const visited = new Set<string>();
  const result: LineItemTaxSchema[] = [];

  function visit(tax: LineItemTaxSchema) {
    if (visited.has(tax.id)) return;
    visited.add(tax.id);

    // visit dependencies first
    for (const ct of tax.cumulativeTaxes ?? []) {
      const found = map.get(ct.id);
      if (found) visit(found);
    }

    result.push(tax);
  }

  for (const tax of taxes) visit(tax);

  return result;
}

export const getDateFnsLocale = (locale: string) => {
  switch (locale) {
    case "en":
      return enUS;
    case "fr":
      return fr;
    case "de":
      return de;
    case "es":
      return es;
    case "it":
      return it;
    default:
      return enUS;
  }
};

/**
 * The receipt extractor returns no confidence score, so this reports field
 * coverage instead: how many of the fields the expense form needs were read.
 * Shared by the create/review drawer and the expense detail page.
 */
export const extractionConfidence = (data: Partial<ExtractDataFromExpense>): number => {
  const keys: (keyof ExtractDataFromExpense)[] = [
    "relationName",
    "date",
    "total",
    "currency",
    "expenseNumber",
    "subtotal",
    "taxAmount",
  ];
  const present = keys.filter((key) => data[key] !== null && data[key] !== undefined && data[key] !== "").length;
  return Math.min(99, Math.max(40, Math.round((present / keys.length) * 100)));
};
